import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import axios from 'axios';
import { getDeviceKey } from '@/services/device/deviceCredential';
import {
  deleteLocalHubCredential,
  getLocalHubCredential,
  saveLocalHubCredential,
} from './hubCredentialStore';
import {
  HUB_DISCOVERY_STATES,
  targetAddressSpaceForUrl,
} from './hubDiscoveryService';

function token(store) {
  return store.state.partner_token || getPartnerToken();
}

export async function postToHub(url, payload, timeoutMs) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      mode: 'cors',
      signal: controller.signal,
      targetAddressSpace: targetAddressSpaceForUrl(url),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(
        data?.error || data?.detail || `El Hub respondió HTTP ${response.status}.`,
      );
      error.response = { status: response.status, data };
      throw error;
    }
    return data;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function pairWithHub(store, discovery, deviceName) {
  if (!discovery?.url || !discovery?.profile?.hub_uuid) {
    throw new Error('Primero hay que localizar el Local Hub.');
  }
  store.commit('device/setLocalControlDiscovery', {
    ...discovery,
    state: HUB_DISCOVERY_STATES.PAIRING,
  });
  const state = store.state.device;
  const deviceKey = await getDeviceKey(state.deviceId);
  const grantResponse = await axios.post(
    `${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/device-pairing-grant`,
    { hub_uuid: discovery.profile.hub_uuid },
    {
      headers: {
        Authorization: `Bearer ${token(store)}`,
        'X-EWU-Device-Key': deviceKey,
      },
      timeout: 5000,
    },
  );
  const pairResponse = await postToHub(
    `${discovery.url}/local/v1/pair`,
    {
      pairing_grant: grantResponse.data.pairing_grant,
      device_name: deviceName || state.device?.name || 'Dispositivo EWU',
      frontend_version: process.env.VUE_APP_VERSION || '0.1.0',
    },
    5000,
  );
  const credential = {
    hub_uuid: discovery.profile.hub_uuid,
    device_uuid: state.deviceId,
    local_device_id: pairResponse.local_device_id,
    local_device_secret: pairResponse.local_device_secret,
    control_protocol_version: pairResponse.control_protocol_version,
    grant_contract_version: 3,
    offline_grant_expires_at:
      grantResponse.data.offline_grant_expires_at || null,
    paired_at: new Date().toISOString(),
  };
  await saveLocalHubCredential(credential);
  store.commit('device/setLocalControlDiscovery', {
    ...discovery,
    state: HUB_DISCOVERY_STATES.CONNECTED,
    paired: true,
  });
  return credential;
}

export async function unpairFromHub(discovery) {
  const hubUuid = discovery?.profile?.hub_uuid;
  const credential = await getLocalHubCredential(hubUuid);
  if (!credential || !discovery?.url) return false;
  await postToHub(
    `${discovery.url}/local/v1/unpair`,
    {
      local_device_id: credential.local_device_id,
      local_device_secret: credential.local_device_secret,
    },
    4000,
  );
  await deleteLocalHubCredential(hubUuid);
  return true;
}
